import React, { useEffect, useState, useCallback } from 'react'
import { FaSave, FaHistory, FaShieldAlt, FaFileContract } from 'react-icons/fa'
import MDEditor from '@uiw/react-md-editor'
import { toast } from 'react-toastify'
import cancellationPolicyService from '../services/cancellationPolicyService'
import Loader from './Loader'
import Button from './ui/Button'

const POLICY_TYPES = [
  { key: 'cancellation', label: 'Cancellation Policy', icon: <FaShieldAlt /> },
  { key: 'terms_and_conditions', label: 'Terms & Conditions', icon: <FaFileContract /> },
]

const PolicySettings = () => {
  const [activeType, setActiveType] = useState('cancellation')
  const [content, setContent] = useState('')
  const [activePolicy, setActivePolicy] = useState(null)
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [previewing, setPreviewing] = useState(null) // version row being previewed

  const loadAll = useCallback(async (type) => {
    setLoading(true)
    try {
      const [activeResp, listResp] = await Promise.all([
        cancellationPolicyService.getActive(type),
        cancellationPolicyService.list(type, 50),
      ])
      const policy = activeResp?.policy || null
      setActivePolicy(policy)
      setHistory(listResp?.policies || [])
      // Pre-fill the editor with the current active content (or blank if none).
      if (policy?.id) {
        const detail = await cancellationPolicyService.getById(policy.id)
        setContent(detail?.policy?.content || '')
      } else {
        setContent('')
      }
    } catch (err) {
      console.error('load_policies_error', err)
      toast.error('Failed to load policies')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadAll(activeType)
    setPreviewing(null)
  }, [activeType, loadAll])

  const handlePublish = async () => {
    const trimmed = (content || '').trim()
    if (!trimmed) {
      toast.warn('Content cannot be empty')
      return
    }
    setSaving(true)
    try {
      await cancellationPolicyService.publish(activeType, trimmed)
      toast.success('New version published — applies to all new bookings.')
      await loadAll(activeType)
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to publish'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  const handlePreviewHistory = async (id) => {
    try {
      const resp = await cancellationPolicyService.getById(id)
      setPreviewing(resp?.policy || null)
    } catch (err) {
      console.error('preview_history_error', err)
      toast.error('Failed to load version')
    }
  }

  const fmtDate = (iso) => {
    if (!iso) return '—'
    try {
      return new Date(iso).toLocaleString()
    } catch {
      return iso
    }
  }

  return (
    <div className="space-y-6">
      {/* Type switcher */}
      <div className="flex gap-2">
        {POLICY_TYPES.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setActiveType(t.key)}
            className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition ${
              activeType === t.key
                ? 'bg-primary-600 text-white shadow'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <Loader />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Editor + active version */}
          <div className="lg:col-span-2 space-y-4">
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="mb-2 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Edit {POLICY_TYPES.find((t) => t.key === activeType).label}
                  </h3>
                  <p className="text-xs text-gray-500">
                    Currently active:{' '}
                    {activePolicy ? (
                      <>
                        <span className="font-mono">v{activePolicy.version}</span> ·{' '}
                        {fmtDate(activePolicy.created_at)}
                      </>
                    ) : (
                      <em>None published yet</em>
                    )}
                  </p>
                </div>
                <Button
                  variant="primary"
                  icon={<FaSave />}
                  onClick={handlePublish}
                  disabled={saving}
                >
                  {saving ? 'Publishing…' : 'Publish New Version'}
                </Button>
              </div>
              <div data-color-mode="light">
                <MDEditor
                  value={content}
                  onChange={(v) => setContent(v || '')}
                  height={400}
                  preview="live"
                />
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Saving creates a new immutable version (CalVer{' '}
                <span className="font-mono">YYYY.MM.DD.HHMM</span> UTC). The previous active version
                is preserved and remains linked to historical bookings.
              </p>
            </div>
          </div>

          {/* Version history */}
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
              <FaHistory /> Version History
            </h3>
            {history.length === 0 ? (
              <p className="text-sm text-gray-500">No versions yet.</p>
            ) : (
              <ul className="space-y-2 max-h-[480px] overflow-y-auto">
                {history.map((row) => (
                  <li
                    key={row.id}
                    className={`rounded-lg border p-3 text-sm ${
                      row.is_active ? 'border-primary-300 bg-primary-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs">{row.version}</span>
                      {row.is_active && (
                        <span className="rounded-full bg-primary-600 px-2 py-0.5 text-xs text-white">
                          Active
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      {row.created_by_name || 'Unknown'} · {fmtDate(row.created_at)}
                    </div>
                    <button
                      type="button"
                      className="mt-2 text-xs text-primary-700 hover:underline"
                      onClick={() => handlePreviewHistory(row.id)}
                    >
                      Preview
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Preview modal-ish panel */}
          {previewing && (
            <div className="lg:col-span-3 rounded-xl border border-gray-200 bg-white p-4">
              <div className="mb-3 flex items-center justify-between">
                <h4 className="font-semibold text-gray-900">
                  Preview: <span className="font-mono">v{previewing.version}</span>
                </h4>
                <button
                  type="button"
                  className="text-sm text-gray-500 hover:text-gray-800"
                  onClick={() => setPreviewing(null)}
                >
                  Close
                </button>
              </div>
              <div data-color-mode="light">
                <MDEditor.Markdown source={previewing.content || ''} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default PolicySettings
